import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { orders as ordersService } from "../services/supabase";
import { Order } from "../types/database";
import "./orders.css";

// T-shirt front images
import tshirtArcticWhiteFront from "../assets/t-shirts/Front/JC001-ARCTIC-WHITE-1776042201.webp";
import tshirtCharcoalFront from "../assets/t-shirts/Front/JC001-CHARCOAL-1776042221.webp";
import tshirtElectricPinkFront from "../assets/t-shirts/Front/JC001-ELECTRIC-PINK-1776042252.webp";
import tshirtHawaiianBlueFront from "../assets/t-shirts/Front/JC001-HAWAIIAN-BLUE-1776042275.webp";
import tshirtJadeFront from "../assets/t-shirts/Front/JC001-JADE-1776042296.webp";
import tshirtJetBlackFront from "../assets/t-shirts/Front/JC001-JET-BLACK-1776042300.webp";
import tshirtMagentaMagicFront from "../assets/t-shirts/Front/JC001-MAGENTA-MAGIC-1776042315.webp";
import tshirtOrangeCrushFront from "../assets/t-shirts/Front/JC001-ORANGE-CRUSH-1776042327.webp";
// @ts-ignore
import tshirtFrenchNavyFront from "../assets/t-shirts/Front/JC001J FRENCH NAVY (FRONT).webp";

// Polo front images
const poloWhiteFront = require("../assets/Polos/TR505_White_FRONTo.webp");
const poloRedFront = require("../assets/Polos/TR505_RED_FRONTo.webp");
const poloNavyFront = require("../assets/Polos/TR505_FrenchNavy_FRONTo.webp");
const poloBlackFront = require("../assets/Polos/TR505_Black_FRONTo.webp");

const TSHIRT_FRONT_MAP: Record<string, string> = {
  "#F2F2F0": tshirtArcticWhiteFront, "#4A4A4A": tshirtCharcoalFront,
  "#FF1493": tshirtElectricPinkFront, "#0099CC": tshirtHawaiianBlueFront,
  "#00A878": tshirtJadeFront, "#111111": tshirtJetBlackFront,
  "#CC00CC": tshirtMagentaMagicFront, "#FF5500": tshirtOrangeCrushFront,
  "#1E3A8A": tshirtFrenchNavyFront,
};

const POLO_FRONT_MAP: Record<string, string> = {
  "#FFFFFF": poloWhiteFront, "#DC2626": poloRedFront,
  "#1E3A8A": poloNavyFront, "#000000": poloBlackFront,
};

const getGarmentImage = (kitType: string | null, color: string | null): string => {
  const isPolo = kitType === "polo";
  const map = isPolo ? POLO_FRONT_MAP : TSHIRT_FRONT_MAP;
  const fallback = isPolo ? poloWhiteFront : tshirtArcticWhiteFront;
  return (color && map[color]) || fallback;
};

const Orders: React.FC = () => {
  const { user, username } = useUser();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    if (!user?.email) {
      setLoading(false);
      return;
    }

    const fetchOrders = async () => {
      try {
        const { data, error } = await ordersService.getByUser(user.email!);
        if (error) throw error;
        setOrders(data || []);
      } catch (err) {
        console.error("Failed to fetch orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [user]);

  if (!user) {
    return (
      <div className="orders-page">
        <div className="op-empty">
          <div className="op-empty-num">00</div>
          <div className="op-empty-title">Not Logged In</div>
          <p className="op-empty-desc">Please sign in to view your orders.</p>
          <button onClick={() => navigate("/account")} className="op-btn-primary">
            Sign In
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="orders-page">
        <p className="op-loading">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="orders-page">

      <div className="op-header">
        <div className="op-section-num">[ MY ORDERS ]</div>
        <h1 className="op-title">
          {username ? `${username}'s Orders` : "My Orders"}
        </h1>
      </div>

      {orders.length === 0 ? (
        <div className="op-empty">
          <div className="op-empty-num">00</div>
          <div className="op-empty-title">No Orders Yet</div>
          <p className="op-empty-desc">
            Your order history will appear here once you place your first order.
          </p>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "tribes" } })}
            className="op-btn-primary"
          >
            Browse Teams
          </button>
        </div>
      ) : (
        <div className="op-list">
          {orders.map((order) => {
            const quantities = order.quantities || {};
            const totalItems = Object.values(quantities).reduce(
              (sum, qty) => sum + (parseInt(qty.toString()) || 0),
              0
            );
            const status = order.status || 'pending';

            return (
              <div key={order.id} className="op-card">

                {/* Kit preview */}
                <div className="op-preview">
                  <img
                    className="op-preview-img"
                    src={getGarmentImage(order.kit_type, order.teamwear_color)}
                    alt={order.kit_type === 'polo' ? 'Polo shirt' : 'T-shirt'}
                  />
                </div>

                {/* Order details */}
                <div className="op-details">
                  <div className="op-design-name">
                    {order.design_name || "Custom Kit"}
                  </div>
                  <div className="op-order-id">Order #{order.order_id}</div>

                  <div className="op-row">
                    <span className="op-row-key">Date</span>
                    <span className="op-row-val">
                      {new Date(order.created_at).toLocaleDateString("en-GB", {
                        day: "numeric", month: "short", year: "numeric"
                      })}
                    </span>
                  </div>
                  <div className="op-row">
                    <span className="op-row-key">Items</span>
                    <span className="op-row-val">{totalItems} units</span>
                  </div>
                  <div className="op-row">
                    <span className="op-row-key">Status</span>
                    <span className={`op-status ${status}`}>{status}</span>
                  </div>
                  {order.team_name && (
                    <div className="op-row">
                      <span className="op-row-key">Team</span>
                      <span className="op-row-val">{order.team_name}</span>
                    </div>
                  )}
                  <div className="op-row">
                    <span className="op-row-key">Colour</span>
                    <span className="op-row-val">
                      <span
                        className="op-color-swatch"
                        style={{ backgroundColor: order.teamwear_color || "#1E3A8A" }}
                      />
                      {order.teamwear_color || "#1E3A8A"}
                    </span>
                  </div>
                  {order.back_print_enabled && order.back_print_text && (
                    <div className="op-row">
                      <span className="op-row-key">Back Print</span>
                      <span className="op-row-val">"{order.back_print_text}"</span>
                    </div>
                  )}
                </div>

                {/* Quantities & pricing */}
                <div className="op-pricing">
                  <div className="op-pricing-label">Quantities</div>

                  {Object.entries(quantities).map(([size, qty]) =>
                    qty > 0 ? (
                      <div key={size} className="op-qty-row">
                        <span className="op-qty-size">{size}</span>
                        <span className="op-qty-val">{qty} units</span>
                      </div>
                    ) : null
                  )}

                  <div className="op-pricing-divider" />

                  <div className="op-price-row">
                    <span className="op-price-key">Subtotal</span>
                    <span className="op-price-val">
                      £{parseFloat(order.subtotal.toString()).toFixed(2)}
                    </span>
                  </div>
                  <div className="op-price-row">
                    <span className="op-price-key">Tax</span>
                    <span className="op-price-val">
                      £{parseFloat(order.tax.toString()).toFixed(2)}
                    </span>
                  </div>

                  <div className="op-total-row">
                    <span className="op-total-key">Total</span>
                    <span className="op-total-val">
                      £{parseFloat(order.total.toString()).toFixed(2)}
                    </span>
                  </div>
                </div>

              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Orders;
